import React, { FunctionComponent, ReactNode } from 'react'
import { CheckoutState, useAppRouter, useCheckoutState, useCrossBorderTrade, useExperiment } from '../../state'
import styles from '../OrderDetails/style.module.css'
import { Divider, Grid, Skeleton, Text, DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { Money } from '../Money'
import { money } from '../../model'
import { useCheckoutSizes } from '../../utils/responsive'
import { Trans, useTranslation } from '../../utils/convergence'

import { Money as MoneyModel } from '@afterpay/types'

interface OrderConversionBreakdownProps {
  location: 'summary' | 'detail'
}

export const OrderConversionBreakdown: FunctionComponent<OrderConversionBreakdownProps> = (props) => {
  const { isCrossBorder, exchangeRate } = useCrossBorderTrade()
  const isExchangeRateInDropdownExperiment = useExperiment('ExchangeRateInDropdown', true)
  const { locale } = useAppRouter()
  const { textSize } = useCheckoutSizes()
  const state: CheckoutState = useCheckoutState()
  const { t } = useTranslation()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const renderOrderBreakdown = (label: string, value: MoneyModel | undefined, loading: boolean, locale): ReactNode => {
    return (
      <div className={styles.amountBreakdown}>
        <Grid>
          <Text bold size={textSize}>
            {label}
          </Text>
          <Grid.FixedItem width={180} align='right'>
            {typeof value !== 'undefined' ? (
              <Money locale={locale} size={textSize} value={value} loading={loading} testNameSpace={`label`} />
            ) : (
              <Text renderAs='div' size={textSize}>
                <span className={loading ? styles.visuallyInvisible : ''}>{t('summary:includedInTotal')}</span>
                {loading && <Skeleton />}
              </Text>
            )}
          </Grid.FixedItem>
          <Grid.FixedItem width={16} />
        </Grid>
      </div>
    )
  }

  const renderAmountConverted = (): ReactNode => {
    return renderOrderBreakdown(t('summary:amountConverted'), state.started?.merchantAmount, false, locale)
  }

  const renderConversionRate = (): ReactNode => {
    if (!exchangeRate) {
      return null
    }

    return (
      <div className={styles.forex}>
        <Grid>
          <Text bold size={textSize}>
            {t('summary:conversionRate')}
          </Text>
          <Grid.FixedItem width={180} align='right'>
            <Trans
              i18nKey='orders:orderSummary.exchangeRate'
              components={{
                consumerAmount: <Money locale={locale} size={textSize} value={money('1', state.started?.consumerAmount.currency)} />,
                merchantAmount: <Money locale={locale} decimalPlaces={3} size={textSize} value={money(exchangeRate, state.started?.merchantAmount.currency)} />,
              }}
            />
          </Grid.FixedItem>
          <Grid.FixedItem width={16} />
        </Grid>
      </div>
    )
  }

  const renderToTal = (): ReactNode => {
    return renderOrderBreakdown(t('summary:total'), state.started?.consumerAmount, false, locale)
  }

  const renderExchangeRateOriginal = (): ReactNode => {
    if (isArcadeDesignLanguage) {
      return null
    }

    return (
      <div className={styles.forex}>
        <Text color='Gray40' className={styles.moneyText}>
          <Trans
            i18nKey='orders:orderSummary:exchange'
            values={{ exchange: exchangeRate }}
            components={{ amount: <Money locale={locale} size={textSize} value={state.started?.merchantAmount} /> }}
          />
        </Text>
      </div>
    )
  }

  const renderConversionBreakdown = (): ReactNode => {
    return (
      <>
        <Divider kind='Hairline' />
        {renderAmountConverted()}
        {renderConversionRate()}
        {renderToTal()}
      </>
    )
  }

  const renderExchangeRateInOrderSummaryHeader = (): ReactNode => {
    switch (isExchangeRateInDropdownExperiment.value) {
      case 'A':
        return null
      case 'B':
      case 'C':
        return renderExchangeRateOriginal()
      case 'D':
        return renderConversionBreakdown()
      case 'E':
        return null
    }
  }

  const renderExchangeRateInOrderDetail = (): ReactNode => {
    switch (isExchangeRateInDropdownExperiment.value) {
      case 'A':
      case 'B':
        return renderConversionBreakdown()
      case 'C':
      case 'D':
      case 'E':
        return null
    }
  }

  const renderExchangeRate = (): ReactNode => {
    if (!isCrossBorder) {
      return null
    }
    if (props.location === 'summary') {
      return renderExchangeRateInOrderSummaryHeader()
    }
    return renderExchangeRateInOrderDetail()
  }

  return <>{renderExchangeRate()}</>
}
